import { useState, useEffect, useCallback } from 'react';
import { costingAPI } from '@/lib/api';
import { useAuth } from '@/lib/auth';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { toast } from 'sonner';
import { RefreshCw, Save, AlertTriangle, Loader2 } from 'lucide-react';

function MarginBadge({ pct }) {
  const color = pct >= 40 ? '#059669' : pct >= 20 ? '#D97706' : '#DC2626';
  const bg = pct >= 40 ? 'rgba(16,185,129,0.08)' : pct >= 20 ? 'rgba(245,158,11,0.08)' : 'rgba(239,68,68,0.08)';
  return (
    <Badge variant="outline" className="text-xs px-2 py-0.5 font-bold tabular-nums"
      style={{ backgroundColor: bg, color, borderColor: 'transparent' }} data-testid="margin-badge">
      {pct}%
    </Badge>
  );
}

function CostRow({ label, estimated, actual, showVariance = true }) {
  const hasActual = actual != null && actual !== undefined;
  const variance = hasActual && estimated > 0 ? Math.round(((actual - estimated) / estimated) * 100) : null;
  const highVariance = variance != null && Math.abs(variance) > 15;

  return (
    <tr className="border-b last:border-0" style={{ borderColor: 'rgba(27,42,74,0.04)' }}>
      <td className="py-2 pr-3 text-xs" style={{ color: 'var(--ink-muted)' }}>{label}</td>
      <td className="py-2 px-3 text-xs text-right tabular-nums font-medium" style={{ color: 'var(--navy-ink)' }}>
        {estimated != null ? `₹${estimated.toLocaleString('en-IN')}` : '—'}
      </td>
      <td className="py-2 px-3 text-xs text-right tabular-nums font-medium" style={{ color: hasActual ? 'var(--brand-navy)' : 'var(--ink-muted)' }}>
        {hasActual ? `₹${actual.toLocaleString('en-IN')}` : '—'}
      </td>
      <td className="py-2 pl-3 text-right">
        {showVariance && highVariance && (
          <Badge variant="outline" className="text-[9px] px-1 py-0 font-bold gap-0.5"
            style={{ color: variance > 0 ? '#DC2626' : '#059669', backgroundColor: variance > 0 ? 'rgba(239,68,68,0.06)' : 'rgba(16,185,129,0.06)' }}>
            <AlertTriangle className="w-2.5 h-2.5" />
            {variance > 0 ? '+' : ''}{variance}%
          </Badge>
        )}
      </td>
    </tr>
  );
}

export default function CostMarginTab({ orderId }) {
  const { user } = useAuth();
  const isAdmin = user?.role === 'Admin';
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [recalculating, setRecalculating] = useState(false);
  const [manualEdits, setManualEdits] = useState({ shipping_cost: '', customs_duty: '', overhead_allocation: '' });

  const fetchData = useCallback(async () => {
    if (!isAdmin) return;
    try {
      const res = await costingAPI.get(orderId);
      setData(res.data);
      setManualEdits({
        shipping_cost: res.data.shipping_cost ?? 0,
        customs_duty: res.data.customs_duty ?? 0,
        overhead_allocation: res.data.overhead_allocation ?? 1200,
      });
    } catch (err) {
      if (err.response?.status !== 403) console.error(err);
    }
    setLoading(false);
  }, [orderId, isAdmin]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleRecalculate = async () => {
    setRecalculating(true);
    try {
      const res = await costingAPI.recalculate(orderId);
      setData(res.data);
      toast.success('Cost sheet recalculated');
    } catch { toast.error('Failed'); }
    setRecalculating(false);
  };

  const handleSaveManual = async () => {
    setSaving(true);
    try {
      const res = await costingAPI.update(orderId, {
        shipping_cost: parseFloat(manualEdits.shipping_cost) || 0,
        customs_duty: parseFloat(manualEdits.customs_duty) || 0,
        overhead_allocation: parseFloat(manualEdits.overhead_allocation) || 0,
      });
      setData(res.data);
      toast.success('Manual costs updated');
    } catch { toast.error('Failed to save'); }
    setSaving(false);
  };

  if (!isAdmin) return (
    <p className="text-sm py-8 text-center" style={{ color: 'var(--ink-muted)' }}>Admin access required</p>
  );

  if (loading) return (
    <div className="space-y-3"><Skeleton className="h-20" /><Skeleton className="h-48" /></div>
  );

  if (!data) return (
    <p className="text-sm py-8 text-center" style={{ color: 'var(--ink-muted)' }}>No cost data available</p>
  );

  const d = data;

  return (
    <div className="space-y-4" data-testid="cost-margin-tab">
      {/* Margin Summary Cards */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <Card className="rounded-xl border bg-white" data-testid="gross-margin-card">
          <CardContent className="p-3 text-center">
            <p className="text-[10px] font-semibold uppercase tracking-wide" style={{ color: 'var(--ink-muted)' }}>Gross Margin</p>
            <div className="flex items-center justify-center gap-2 mt-1">
              <span className="text-2xl font-bold tabular-nums" style={{ color: 'var(--brand-navy)' }}>
                ₹{(d.gross_margin_actual ?? d.gross_margin_estimated)?.toLocaleString('en-IN')}
              </span>
              <MarginBadge pct={d.gross_margin_pct_actual ?? d.gross_margin_pct_estimated} />
            </div>
          </CardContent>
        </Card>
        <Card className="rounded-xl border bg-white" data-testid="net-margin-card">
          <CardContent className="p-3 text-center">
            <p className="text-[10px] font-semibold uppercase tracking-wide" style={{ color: 'var(--ink-muted)' }}>Net Margin</p>
            <div className="flex items-center justify-center gap-2 mt-1">
              <span className="text-2xl font-bold tabular-nums" style={{ color: 'var(--brand-navy)' }}>
                ₹{(d.net_margin_actual ?? d.net_margin_estimated)?.toLocaleString('en-IN')}
              </span>
              <MarginBadge pct={d.net_margin_pct_actual ?? d.net_margin_pct_estimated} />
            </div>
          </CardContent>
        </Card>
        <Card className="rounded-xl border bg-white" data-testid="price-charged-card">
          <CardContent className="p-3 text-center">
            <p className="text-[10px] font-semibold uppercase tracking-wide" style={{ color: 'var(--ink-muted)' }}>Price Charged</p>
            <p className="text-2xl font-bold tabular-nums mt-1" style={{ color: 'var(--brand-navy)' }}>
              ₹{d.price_charged?.toLocaleString('en-IN')}
            </p>
          </CardContent>
        </Card>
        <Card className="rounded-xl border bg-white" data-testid="total-cost-card">
          <CardContent className="p-3 text-center">
            <p className="text-[10px] font-semibold uppercase tracking-wide" style={{ color: 'var(--ink-muted)' }}>Total Cost</p>
            <p className="text-2xl font-bold tabular-nums mt-1" style={{ color: 'var(--navy-ink)' }}>
              ₹{(d.total_cost_actual ?? d.total_cost_estimated)?.toLocaleString('en-IN')}
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Cost Breakdown Table */}
      <Card className="rounded-xl border bg-white overflow-hidden" data-testid="cost-breakdown-table">
        <CardHeader className="pb-1 pt-3 px-4 flex flex-row items-center justify-between">
          <CardTitle className="text-sm font-semibold" style={{ color: 'var(--brand-navy)' }}>Cost Breakdown</CardTitle>
          <Button variant="ghost" size="sm" className="h-7 gap-1 text-xs" onClick={handleRecalculate} disabled={recalculating}
            data-testid="recalculate-btn">
            {recalculating ? <Loader2 className="w-3 h-3 animate-spin" /> : <RefreshCw className="w-3 h-3" />}
            Recalculate
          </Button>
        </CardHeader>
        <CardContent className="px-4 pb-4">
          <table className="w-full">
            <thead>
              <tr style={{ borderBottom: '1px solid rgba(27,42,74,0.08)' }}>
                <th className="text-left py-2 text-[10px] font-semibold uppercase tracking-wide" style={{ color: 'var(--ink-muted)' }}>Cost Item</th>
                <th className="text-right py-2 px-3 text-[10px] font-semibold uppercase tracking-wide" style={{ color: 'var(--ink-muted)' }}>Estimated</th>
                <th className="text-right py-2 px-3 text-[10px] font-semibold uppercase tracking-wide" style={{ color: 'var(--ink-muted)' }}>Actual</th>
                <th className="text-right py-2 pl-3 text-[10px] font-semibold uppercase tracking-wide" style={{ color: 'var(--ink-muted)' }}>Variance</th>
              </tr>
            </thead>
            <tbody>
              <CostRow label="Fabric" estimated={d.fabric_cost_estimated} actual={d.fabric_cost_actual} />
              <CostRow label="Trims" estimated={d.trims_cost} actual={d.trims_cost} showVariance={false} />
              <CostRow label="Karigar Labour" estimated={d.karigar_labour_estimated} actual={d.karigar_labour_actual > 0 ? d.karigar_labour_actual : null} />
              <CostRow label="Pattern Cutting" estimated={d.pattern_cutting_cost_estimated} actual={d.pattern_cutting_cost_actual > 0 ? d.pattern_cutting_cost_actual : null} />
              <CostRow label="Alterations" estimated={d.alteration_cost_estimated} actual={d.alteration_cost_actual > 0 ? d.alteration_cost_actual : null} />
              <CostRow label="Outsourcing" estimated={d.outsource_cost} actual={d.outsource_cost} showVariance={false} />
              <CostRow label="Shipping" estimated={d.shipping_cost} actual={d.shipping_cost} showVariance={false} />
              <CostRow label="Customs Duty" estimated={d.customs_duty} actual={d.customs_duty} showVariance={false} />
              <CostRow label="Overhead" estimated={d.overhead_allocation} actual={d.overhead_allocation} showVariance={false} />
            </tbody>
            <tfoot>
              <tr style={{ borderTop: '2px solid rgba(27,42,74,0.12)' }}>
                <td className="py-2 text-xs font-bold" style={{ color: 'var(--brand-navy)' }}>Total</td>
                <td className="py-2 px-3 text-xs text-right tabular-nums font-bold" style={{ color: 'var(--brand-navy)' }}>
                  ₹{d.total_cost_estimated?.toLocaleString('en-IN')}
                </td>
                <td className="py-2 px-3 text-xs text-right tabular-nums font-bold" style={{ color: 'var(--brand-navy)' }}>
                  ₹{d.total_cost_actual?.toLocaleString('en-IN')}
                </td>
                <td />
              </tr>
            </tfoot>
          </table>
          {d.is_express && d.express_surcharge > 0 && (
            <div className="mt-3 flex items-center justify-between px-1 py-2 rounded-lg" style={{ backgroundColor: 'rgba(217,119,6,0.06)' }}
              data-testid="express-surcharge-revenue">
              <span className="text-xs font-semibold flex items-center gap-1" style={{ color: '#D97706' }}>
                Express Surcharge (Revenue)
              </span>
              <span className="text-xs font-bold tabular-nums" style={{ color: '#D97706' }}>
                +₹{d.express_surcharge?.toLocaleString('en-IN')}
              </span>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Manual Cost Inputs */}
      <Card className="rounded-xl border bg-white" data-testid="manual-costs-card">
        <CardHeader className="pb-1 pt-3 px-4">
          <CardTitle className="text-sm font-semibold" style={{ color: 'var(--brand-navy)' }}>Manual Costs</CardTitle>
        </CardHeader>
        <CardContent className="px-4 pb-4">
          <div className="grid grid-cols-3 gap-3">
            <div>
              <label className="text-[10px] font-semibold uppercase tracking-wide" style={{ color: 'var(--ink-muted)' }}>Shipping (₹)</label>
              <Input type="number" step="0.01" value={manualEdits.shipping_cost}
                onChange={e => setManualEdits(p => ({ ...p, shipping_cost: e.target.value }))}
                className="mt-1 h-8 text-xs" data-testid="shipping-input" />
            </div>
            <div>
              <label className="text-[10px] font-semibold uppercase tracking-wide" style={{ color: 'var(--ink-muted)' }}>Customs Duty (₹)</label>
              <Input type="number" step="0.01" value={manualEdits.customs_duty}
                onChange={e => setManualEdits(p => ({ ...p, customs_duty: e.target.value }))}
                className="mt-1 h-8 text-xs" data-testid="customs-input" />
            </div>
            <div>
              <label className="text-[10px] font-semibold uppercase tracking-wide" style={{ color: 'var(--ink-muted)' }}>Overhead (₹)</label>
              <Input type="number" step="0.01" value={manualEdits.overhead_allocation}
                onChange={e => setManualEdits(p => ({ ...p, overhead_allocation: e.target.value }))}
                className="mt-1 h-8 text-xs" data-testid="overhead-input" />
            </div>
          </div>
          <Button size="sm" className="mt-3 gap-1 h-8" onClick={handleSaveManual} disabled={saving}
            style={{ backgroundColor: 'var(--brand-navy)' }} data-testid="save-manual-btn">
            {saving ? <Loader2 className="w-3 h-3 animate-spin" /> : <Save className="w-3 h-3" />}
            Save Manual Costs
          </Button>
          <p className="text-[9px] mt-1" style={{ color: 'var(--ink-muted)' }}>
            Last calculated: {d.last_calculated ? new Date(d.last_calculated).toLocaleString('en-IN') : 'Never'}
          </p>
        </CardContent>
      </Card>
    </div>
  );
}
